import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { FileText, Plus, Upload, AlertTriangle, Eye, Trash2, ImagePlus, Camera, X, Pencil, Files, Loader2, GripVertical, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, rectSortingStrategy, sortableKeyboardCoordinates, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ADMIN_ROLES = new Set(["super_admin", "owner_director", "operations_admin"]);

export default function Documents() {
  const t = useT();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [docs, setDocs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [emailingDoc, setEmailingDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const [d, a] = await Promise.all([api.get("/documents"), api.get("/assets")]);
    setDocs(d.data); setAssets(a.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const handleView = (doc) => {
    const pages = doc.file_ids || [];
    if (pages.length === 0) {
      toast.error(t("documents.noFile"));
      return;
    }
    // Combined PDF — single tab with all pages stitched together
    window.open(`${API_BASE}/documents/${doc.id}/combined.pdf`, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(t("documents.deleteConfirm"))) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(t("documents.deletedToast"));
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setShowDialog(true);
  };

  const handleNew = () => {
    setEditingDoc(null);
    setShowDialog(true);
  };

  const today = new Date();
  const isExpiring = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const diff = (d - today) / 86400000;
    return diff >= 0 && diff <= 60;
  };
  const isExpired = (dateStr) => dateStr && new Date(dateStr) < today;

  // Page-level drag & drop — drop anywhere on the page to open the New dialog with files staged.
  const [pageDropActive, setPageDropActive] = useState(false);
  const [stagedDropFiles, setStagedDropFiles] = useState(null);
  const dragCount = useRef(0);
  useEffect(() => {
    const onEnter = (e) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        dragCount.current += 1;
        setPageDropActive(true);
      }
    };
    const onLeave = () => {
      dragCount.current = Math.max(0, dragCount.current - 1);
      if (dragCount.current === 0) setPageDropActive(false);
    };
    const onOver = (e) => { e.preventDefault(); };
    const onDrop = (e) => {
      e.preventDefault();
      dragCount.current = 0;
      setPageDropActive(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) {
        setStagedDropFiles(files);
        setEditingDoc(null);
        setShowDialog(true);
      }
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="documents-page">
      {pageDropActive && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center" data-testid="page-drop-overlay">
          <div className="absolute inset-4 border-2 border-dashed border-[#007AFF] rounded-2xl bg-[#007AFF]/10 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <Upload className="w-12 h-12 mx-auto mb-3 text-[#4DA6FF]" />
              <div className="font-display text-2xl font-bold tracking-tight">{t("documents.pageDrop.title")}</div>
              <div className="font-mono text-xs text-[#94A3B8] mt-2">{t("documents.pageDrop.hint")}</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("documents.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("documents.title")}</h2>
          <p className="text-xs text-[#64748B] mt-1">{t("documents.dragHint")}</p>
        </div>
        <Button onClick={handleNew} data-testid="new-doc-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
          <Plus className="w-4 h-4 mr-1" /> {t("documents.new")}
        </Button>
      </div>

      {loading ? <div className="h-64 ledger-card animate-pulse" /> :
        docs.length === 0 ? (
          <div className="ledger-card p-12 text-center text-[#94A3B8]">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <div className="text-sm">{t("documents.empty")}</div>
          </div>
        ) : (
          <div className="ledger-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#070D1B] border-b border-[#1E293B]">
                <tr className="font-mono text-[10px] tracking-widest text-[#94A3B8]">
                  <th className="text-left px-4 py-3">{t("documents.col.title")}</th>
                  <th className="text-left px-4 py-3">{t("documents.col.type")}</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">{t("documents.col.asset")}</th>
                  <th className="text-left px-4 py-3">{t("documents.col.expiry")}</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">{t("documents.col.uploaded")}</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => {
                  const expired = isExpired(d.expiry_date);
                  const expiring = !expired && isExpiring(d.expiry_date);
                  const pageCount = (d.file_ids || []).length;
                  return (
                    <tr key={d.id} className="border-b border-[#1E293B] hover:bg-[#131C31]" data-testid={`doc-row-${d.id}`}>
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          <span>{d.title}</span>
                          {pageCount > 1 && (
                            <span data-testid={`doc-pages-${d.id}`} className="badge-pill badge-neutral text-[10px] font-mono inline-flex items-center gap-1">
                              <Files className="w-3 h-3" /> {pageCount} {t("documents.pages")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="badge-pill badge-neutral">{d.document_type}</span></td>
                      <td className="px-4 py-3 hidden md:table-cell text-[#94A3B8]">{assets.find(a => a.id === d.asset_id)?.name || "—"}</td>
                      <td className="px-4 py-3">
                        {d.expiry_date ? (
                          <span className={`badge-pill ${expired ? "badge-rejected" : expiring ? "badge-pending" : "badge-approved"}`}>
                            {expired && <AlertTriangle className="w-3 h-3" />}
                            {fmtDate(d.expiry_date)}
                          </span>
                        ) : <span className="text-[#94A3B8]">—</span>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell font-mono text-[11px] text-[#94A3B8]">{fmtDate(d.created_at)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {pageCount > 0 && (
                          <button onClick={() => handleView(d)} data-testid={`doc-view-${d.id}`} title={t("documents.view")}
                            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#4DA6FF] hover:bg-[#131C31] hover:border-[#4DA6FF]/40 mr-1">
                            <Eye className="w-3 h-3" />
                          </button>
                        )}
                        {pageCount > 0 && (
                          <button onClick={() => setEmailingDoc(d)} data-testid={`doc-email-${d.id}`} title={t("documents.emailDocument")}
                            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#34D399] hover:border-[#34D399]/40 mr-1">
                            <Mail className="w-3 h-3" />
                          </button>
                        )}
                        <button onClick={() => handleEdit(d)} data-testid={`doc-edit-${d.id}`} title={t("common.edit")}
                          className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#A78BFA] hover:border-[#A78BFA]/40 mr-1">
                          <Pencil className="w-3 h-3" />
                        </button>
                        {isAdmin && (
                          <button onClick={() => handleDelete(d)} data-testid={`doc-delete-${d.id}`} title={t("common.delete")}
                            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#F87171] hover:border-[#EF4444]/40">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      }

      {showDialog && (
        <DocDialog
          assets={assets}
          editing={editingDoc}
          initialFiles={stagedDropFiles}
          onClose={() => { setShowDialog(false); setEditingDoc(null); setStagedDropFiles(null); }}
          onSaved={() => { setShowDialog(false); setEditingDoc(null); setStagedDropFiles(null); reload(); }}
        />
      )}

      {emailingDoc && (
        <EmailDocDialog
          doc={emailingDoc}
          assetName={assets.find(a => a.id === emailingDoc.asset_id)?.name || ""}
          onClose={() => setEmailingDoc(null)}
        />
      )}
    </div>
  );
}

function DocDialog({ assets, editing, initialFiles, onClose, onSaved }) {
  const t = useT();
  const isEdit = !!editing;
  const [form, setForm] = useState({
    title: editing?.title || "",
    document_type: editing?.document_type || "insurance",
    asset_id: editing?.asset_id || "",
    expiry_date: editing?.expiry_date || "",
  });

  // Unified ordered page list. Each entry is either:
  //   {key, kind: "existing", file_id}
  //   {key, kind: "new",      file: File}
  const tmpIdRef = useRef(0);
  const makeKey = (prefix) => `${prefix}-${++tmpIdRef.current}`;
  const [pages, setPages] = useState(() => {
    const list = [];
    for (const fid of (editing?.file_ids || [])) list.push({ key: makeKey("e"), kind: "existing", file_id: fid });
    for (const f of (initialFiles || [])) list.push({ key: makeKey("n"), kind: "new", file: f });
    return list;
  });

  const [uploading, setUploading] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const inputRef = useRef();
  const cameraRef = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPages(prev => {
      const oldIdx = prev.findIndex(p => p.key === active.id);
      const newIdx = prev.findIndex(p => p.key === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handlePickFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setPages(prev => [...prev, ...arr.map(f => ({ key: makeKey("n"), kind: "new", file: f }))]);
  };

  const removePage = (key) => {
    setPages(prev => prev.filter(p => p.key !== key));
  };

  const handleDropZone = (e) => {
    e.preventDefault();
    setDropActive(false);
    handlePickFiles(e.dataTransfer?.files);
  };

  const submit = async () => {
    if (!form.title) { toast.error(t("documents.dialog.titleRequired")); return; }
    setUploading(true);
    try {
      // Walk the final ordered list; for any 'new' entry, upload it now and replace
      // its placeholder with the freshly issued file_id. This preserves the user's
      // chosen ordering (front, back, last-minute insertion, etc.).
      const finalFileIds = [];
      for (const p of pages) {
        if (p.kind === "existing") {
          finalFileIds.push(p.file_id);
        } else {
          const fd = new FormData();
          fd.append("file", p.file);
          fd.append("linked_type", "document");
          if (editing?.id) fd.append("linked_id", editing.id);
          const { data } = await api.post("/media/upload", fd);
          finalFileIds.push(data.id);
        }
      }

      if (isEdit) {
        const patch = {
          title: form.title,
          document_type: form.document_type,
          file_ids: finalFileIds,
        };
        if (form.asset_id) patch.asset_id = form.asset_id;
        if (form.expiry_date) patch.expiry_date = form.expiry_date;
        await api.patch(`/documents/${editing.id}`, patch);
        toast.success(t("documents.updatedToast"));
      } else {
        const body = { ...form, file_ids: finalFileIds };
        if (!body.asset_id) delete body.asset_id;
        if (!body.expiry_date) delete body.expiry_date;
        await api.post("/documents", body);
        toast.success(t("documents.savedToast"));
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-2xl" data-testid={isEdit ? "edit-doc-dialog" : "new-doc-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {isEdit ? t("documents.dialog.editTitle") : t("documents.dialog.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.dialog.titleField")}</Label>
            <Input value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} data-testid="doc-title-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.dialog.type")}</Label>
              <select value={form.document_type} onChange={(e) => setForm(f => ({...f, document_type: e.target.value}))}
                data-testid="doc-type-select"
                className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
                {["insurance", "registration", "license", "permit", "contract", "service_invoice", "other"].map(dt => <option key={dt} value={dt}>{dt}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.dialog.asset")}</Label>
              <select value={form.asset_id} onChange={(e) => setForm(f => ({...f, asset_id: e.target.value}))}
                data-testid="doc-asset-select"
                className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
                <option value="">{t("common.none")}</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.dialog.expiry")}</Label>
            <Input type="date" value={form.expiry_date} onChange={(e) => setForm(f => ({...f, expiry_date: e.target.value}))} data-testid="doc-expiry-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>

          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8] flex items-center justify-between">
              <span>{t("documents.dialog.pages")}</span>
              <span className="text-[#4DA6FF]">{pages.length} {t("documents.pages")}</span>
            </Label>
            {pages.length > 1 && (
              <div className="text-[10px] font-mono text-[#64748B] mt-1">{t("documents.dialog.reorderHint")}</div>
            )}

            {pages.length > 0 && (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={pages.map(p => p.key)} strategy={rectSortingStrategy}>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="doc-pages-list">
                    {pages.map((p, idx) => (
                      <SortablePageThumb key={p.key} page={p} idx={idx} onRemove={() => removePage(p.key)} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Drop zone */}
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
              onDragLeave={() => setDropActive(false)}
              onDrop={handleDropZone}
              className={`mt-2 border-2 border-dashed rounded-md p-5 text-center cursor-pointer transition-colors ${dropActive ? "border-[#007AFF] bg-[#007AFF]/10" : "border-[#1E293B] hover:border-[#334155]"}`}
              data-testid="doc-upload-dropzone"
            >
              <Upload className="w-5 h-5 mx-auto mb-1 text-[#94A3B8]" />
              <div className="text-xs text-[#94A3B8]">{t("documents.dialog.dropHint")}</div>
              <div className="text-[10px] font-mono text-[#64748B] mt-1">{t("documents.dialog.acceptTypes")}</div>
              <input
                ref={inputRef} type="file" multiple
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                accept="image/*,application/pdf,.heic,.heif"
                onChange={(e) => handlePickFiles(e.target.files)}
                data-testid="doc-upload-input"
              />
              <input
                ref={cameraRef} type="file" accept="image/*" capture="environment"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={(e) => handlePickFiles(e.target.files)}
                data-testid="doc-camera-input"
              />
            </div>
            <div className="mt-2 flex items-center gap-4 flex-wrap">
              <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                data-testid="doc-upload-gallery-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC]">
                <ImagePlus className="w-3.5 h-3.5" />
                {t("documents.dialog.addPage")}
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                data-testid="doc-upload-camera-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC]">
                <Camera className="w-3.5 h-3.5" />
                {t("assets.fields.takePhoto")}
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={uploading || (pages.length === 0 && !isEdit)} data-testid="doc-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            {uploading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {uploading ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SortablePageThumb({ page, idx, onRemove }) {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  // Image preview source
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (page.kind === "existing") {
      setSrc(`${API_BASE}/media/${page.file_id}/download`);
    } else if (page.file?.type?.startsWith("image/")) {
      const url = URL.createObjectURL(page.file);
      setSrc(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setSrc(null);
    }
  }, [page]);

  const isNew = page.kind === "new";
  const filename = isNew ? page.file?.name : null;
  const labelClass = isNew
    ? "bg-[#007AFF]/80 text-white"
    : "bg-black/60 text-white";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group bg-[#050A14] border ${isNew ? "border-[#007AFF]/40" : "border-[#1E293B]"} rounded-md overflow-hidden aspect-[3/4]`}
      data-testid={`page-thumb-${idx}`}
      data-page-key={page.key}
      data-page-kind={page.kind}
    >
      {src ? (
        <img src={src} alt={filename || `page ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
          <FileText className="w-8 h-8 text-[#4DA6FF] mb-1" />
          {filename && <div className="text-[10px] font-mono text-[#94A3B8] break-all line-clamp-2">{filename}</div>}
        </div>
      )}

      {/* Drag handle — top-left, always visible */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`page-drag-handle-${idx}`}
        title={t("documents.dialog.dragToReorder")}
        className="absolute top-1 left-1 p-1 rounded-md bg-black/60 text-white cursor-grab active:cursor-grabbing touch-none hover:bg-black/80"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>

      {/* Bottom label: 'Page N' + NEW tag */}
      <div className={`absolute inset-x-0 bottom-0 px-2 py-1 text-[10px] font-mono flex items-center justify-between ${labelClass}`}>
        <span>{t("documents.pageNum", { n: idx + 1 })}</span>
        {isNew && <span className="uppercase">{t("documents.pageNew")}</span>}
      </div>

      {/* Remove button */}
      <button
        onClick={onRemove}
        type="button"
        data-testid={`page-remove-${idx}`}
        className="absolute top-1 right-1 p-1 rounded-md bg-[#EF4444] text-white opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function EmailDocDialog({ doc, assetName, onClose }) {
  const t = useT();
  const pageCount = (doc.file_ids || []).length;
  const defaultSubject = `LaSovita Ledger — ${doc.title}`;
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!toEmail || !/^\S+@\S+\.\S+$/.test(toEmail)) {
      toast.error(t("documents.email.invalidEmail"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/email`, {
        to_email: toEmail,
        subject: subject || undefined,
        message: message || undefined,
      });
      toast.success(`${t("documents.email.sentToast")} → ${data.to}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="email-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#34D399]" />
            {t("documents.email.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Compact summary of what's about to be sent */}
          <div className="ledger-card !bg-[#050A14] p-3 text-xs space-y-1" data-testid="email-doc-summary">
            <div className="font-mono text-[10px] text-[#94A3B8] tracking-widest">{t("documents.email.summary")}</div>
            <div className="font-medium">{doc.title}</div>
            <div className="text-[#94A3B8]">
              {doc.document_type}
              {assetName && <> · {assetName}</>}
              {doc.expiry_date && <> · {t("documents.col.expiry").toLowerCase()}: {doc.expiry_date}</>}
            </div>
            <div className="font-mono text-[10px] text-[#4DA6FF]">{pageCount} {t("documents.pages")} · PDF</div>
          </div>

          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.email.toLabel")}</Label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="broker@example.com"
              data-testid="email-doc-to-input"
              className="bg-[#050A14] border-[#1E293B] mt-1"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.email.subjectLabel")}</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-doc-subject-input"
              className="bg-[#050A14] border-[#1E293B] mt-1"
            />
          </div>

          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("documents.email.messageLabel")}</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={t("documents.email.messagePlaceholder")}
              data-testid="email-doc-message-input"
              className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={send} disabled={sending} data-testid="email-doc-send-btn" className="bg-[#34D399] hover:bg-[#10B981] text-[#022C22]">
            {sending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {sending ? t("documents.email.sending") : t("documents.email.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

